// Zod schemas for framework configuration forms.

import { z } from 'zod'

// --- Scoring Grid ---

export const scoringGridSchema = z.object({
  target_likelihood_level_count: z
    .number({ required_error: 'Required' })
    .int('Must be a whole number')
    .min(2, 'Must be at least 2')
    .max(10, 'Must be 10 or fewer'),
  target_impact_level_count: z
    .number({ required_error: 'Required' })
    .int('Must be a whole number')
    .min(2, 'Must be at least 2')
    .max(10, 'Must be 10 or fewer'),
})

export type ScoringGridFormData = z.infer<typeof scoringGridSchema>

// --- Assessment Thresholds ---

export const assessmentThresholdsSchema = z.object({
  self_assessed_design_days: z
    .number({ required_error: 'Required' })
    .int('Must be a whole number')
    .min(1, 'Must be at least 1 day'),
  self_assessed_operating_days: z
    .number({ required_error: 'Required' })
    .int('Must be a whole number')
    .min(1, 'Must be at least 1 day'),
  independent_design_days: z
    .number({ required_error: 'Required' })
    .int('Must be a whole number')
    .min(1, 'Must be at least 1 day'),
  independent_operating_days: z
    .number({ required_error: 'Required' })
    .int('Must be a whole number')
    .min(1, 'Must be at least 1 day'),
})

export type AssessmentThresholdsFormData = z.infer<typeof assessmentThresholdsSchema>

// --- Evidence Tags ---

export const evidenceTagSchema = z.object({
  tag_name: z
    .string()
    .min(1, 'Tag name is required')
    .max(100, 'Tag name must be 100 characters or fewer'),
  description: z
    .string()
    .max(500, 'Description must be 500 characters or fewer')
    .optional()
    .or(z.literal('')),
})

export type EvidenceTagFormData = z.infer<typeof evidenceTagSchema>

// --- Shared level schema (Likelihood + Impact Tables) ---

export const levelInputSchema = z.object({
  level_number: z
    .number({ required_error: 'Required' })
    .int('Must be a whole number')
    .min(1, 'Must be at least 1'),
  label: z.string().min(1, 'Label is required').max(100, 'Label must be 100 characters or fewer'),
  description: z
    .string()
    .max(500, 'Description must be 500 characters or fewer')
    .optional()
    .or(z.literal('')),
})

export type LevelInputFormData = z.infer<typeof levelInputSchema>

export const levelsFormSchema = z.object({
  levels: z
    .array(levelInputSchema)
    .min(2, 'At least 2 levels are required')
    .max(10, 'Maximum 10 levels'),
})

export type LevelsFormData = z.infer<typeof levelsFormSchema>

// --- Impact table create schema ---

export const impactTableSchema = z.object({
  name: z
    .string()
    .min(1, 'Table name is required')
    .max(100, 'Table name must be 100 characters or fewer'),
  description: z
    .string()
    .max(500, 'Description must be 500 characters or fewer')
    .optional()
    .or(z.literal('')),
})

export type ImpactTableFormData = z.infer<typeof impactTableSchema>

// --- Taxonomy node schema ---

export const taxonomyNodeInputSchema = z.object({
  parent_path: z.string().optional().or(z.literal('')),
  name: z
    .string()
    .min(1, 'Node name is required')
    .max(100, 'Node name must be 100 characters or fewer'),
  description: z
    .string()
    .max(500, 'Description must be 500 characters or fewer')
    .optional()
    .or(z.literal('')),
})

export type TaxonomyNodeInputFormData = z.infer<typeof taxonomyNodeInputSchema>

export const taxonomyVersionSchema = z.object({
  nodes: z.array(taxonomyNodeInputSchema).min(1, 'At least one taxonomy node is required'),
})

export type TaxonomyVersionFormData = z.infer<typeof taxonomyVersionSchema>
